import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertTriangle, FileWarning, Clock3, CheckCircle2, Scale, Coins } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import api from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import ContraventionsView, { CvBadge } from "@/components/common/ContraventionModule";
import ContraventionStats from "@/components/common/ContraventionStats";
import { DeadlineBadge } from "@/components/common/Badges";
import { eur2, fdate, CV_STATUS_META } from "@/lib/format";

function Dashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/contraventions/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">{[...Array(8)].map((_, i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}</div>;

  const kpis = [
    [FileWarning, "Total", d.total, "text-[#001A3B]"],
    [Clock3, "À traiter", d.to_process, "text-amber-600"],
    [AlertTriangle, "En attente", d.pending, "text-orange-600"],
    [CheckCircle2, "Payées", d.paid, "text-emerald-600"],
    [Scale, "Contestées", d.contested, "text-purple-600"],
    [Coins, "Montant total", eur2(d.total_amount), "text-[#001A3B]"],
    [Coins, "Montant payé", eur2(d.paid_amount), "text-emerald-600"],
    [Coins, "Reste à payer", eur2(d.remaining_amount), "text-red-600"],
  ];

  return (
    <div>
      {d.alerts.length > 0 && (
        <div className="mb-5 rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="cv-dashboard-alerts">
          <div className="mb-2 flex items-center gap-2 font-heading font-semibold text-amber-900"><AlertTriangle className="h-5 w-5" />{d.alerts.length} échéance(s) à surveiller dans les 7 prochains jours</div>
          <div className="space-y-1.5">{d.alerts.slice(0, 6).map((a, i) => (
            <div key={i} className="flex items-center gap-3 text-sm text-amber-900">
              <DeadlineBadge level={a.level}>{a.kind}</DeadlineBadge>
              <span className="flex-1 truncate">{a.label}</span>
              <span className="text-xs">{fdate(a.date)} · {a.days < 0 ? `${Math.abs(a.days)} j dépassé` : `dans ${a.days} j`}</span>
            </div>
          ))}</div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {kpis.map(([Icon, label, val, cls]) => (
          <div key={label} className="rounded-2xl border border-[#E2E8F0] bg-white p-4">
            <Icon className={`h-5 w-5 ${cls}`} />
            <div className="mt-2 font-heading text-2xl font-bold text-[#18221F]">{val}</div>
            <div className="text-xs text-[#8695AB]">{label}</div>
          </div>
        ))}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6">
          <h3 className="mb-4 font-heading text-base font-semibold text-[#18221F]">Répartition par statut</h3>
          <div className="space-y-2">{Object.entries(CV_STATUS_META).map(([k, m]) => (
            <div key={k} className="flex items-center gap-3 text-sm">
              <CvBadge status={k} /><span className="ml-auto font-semibold text-[#18221F]">{d.by_status[k] || 0}</span>
            </div>
          ))}</div>
        </div>
        <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6">
          <h3 className="mb-4 font-heading text-base font-semibold text-[#18221F]">Évolution mensuelle</h3>
          <div className="h-56">
            {d.monthly.length === 0 ? <p className="py-16 text-center text-sm text-[#8695AB]">Aucune donnée</p> : (
              <ResponsiveContainer width="100%" height="100%"><BarChart data={d.monthly}><CartesianGrid strokeDasharray="3 3" stroke="#EDF1F6" vertical={false} /><XAxis dataKey="month" tick={{ fontSize: 11, fill: "#8695AB" }} axisLine={false} tickLine={false} /><YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#8695AB" }} axisLine={false} tickLine={false} width={30} /><Tooltip /><Bar dataKey="count" name="Contraventions" fill="#001A3B" radius={[5, 5, 0, 0]} /></BarChart></ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6">
          <h3 className="mb-3 font-heading text-base font-semibold text-[#18221F]">Contraventions par salarié</h3>
          {d.top_employees.length === 0 ? <p className="py-6 text-center text-sm text-[#8695AB]">Aucune donnée</p> : d.top_employees.map((t, i) => (<div key={i} className="flex items-center justify-between border-b border-[#EDF0F5] py-2 text-sm last:border-0"><span>{t.label}</span><span className="font-semibold">{t.count}</span></div>))}
        </div>
        <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6">
          <h3 className="mb-3 font-heading text-base font-semibold text-[#18221F]">Contraventions par véhicule</h3>
          {d.top_vehicles.length === 0 ? <p className="py-6 text-center text-sm text-[#8695AB]">Aucune donnée</p> : d.top_vehicles.map((t, i) => (<div key={i} className="flex items-center justify-between border-b border-[#EDF0F5] py-2 text-sm last:border-0"><span>{t.label}</span><span className="font-semibold">{t.count}</span></div>))}
        </div>
      </div>
    </div>
  );
}

export default function Contraventions() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("onglet") || "liste";
  const setTab = (t) => { const p = new URLSearchParams(params); p.set("onglet", t); setParams(p); };
  const filterList = (extra) => setParams(new URLSearchParams({ onglet: "liste", ...extra }));
  return (
    <div>
      <PageHeader title="Contraventions" subtitle="Suivi des avis de contravention, désignations, paiements et échéances." />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="liste" data-testid="cv-tab-liste">Liste</TabsTrigger>
          <TabsTrigger value="dashboard" data-testid="cv-tab-dashboard">Tableau de bord</TabsTrigger>
          <TabsTrigger value="stats" data-testid="cv-tab-stats">Statistiques PV</TabsTrigger>
        </TabsList>
        <TabsContent value="liste"><ContraventionsView scope={{}} useUrl /></TabsContent>
        <TabsContent value="dashboard"><Dashboard /></TabsContent>
        <TabsContent value="stats"><ContraventionStats onFilterList={filterList} /></TabsContent>
      </Tabs>
    </div>
  );
}
